import {
  FulfillmentTypeListResponseType,
  OrderFulfillmentType,
  OrderPaymentType,
  SupplierStorefrontV2GetResponseType,
} from "@sellar-io/api-v2/types";
import {
  Comparator,
  comparatorNameToComparator,
  getItemName,
  pluralize,
  sanitizeEmailArray,
} from "@sellar-io/common";
import * as ROUTES from "../constants/routes";

import { BasketOrderItem } from "../hooks";
import { withSearchParams } from "./withSearchParams";

export const generatePriceTierOrderRuleDescription = (rule: {
  itemType:
    | "keg"
    | "can"
    | "badge"
    | "cask"
    | "bottle"
    | "any"
    | "draught"
    | "bagInBox"
    | null;
  quantity: number;
  volume?: number | null;
  unit?: "gal" | "L" | "ml" | "cl" | "pt" | "oz" | "g" | "kg" | null;
}): string => {
  if (rule.itemType === "any" || rule.itemType === null) {
    const itemName = pluralize("order item", rule.quantity);
    return `You must purchase at least ${rule.quantity} ${itemName}`;
  }

  const itemName = getItemName({
    itemType: rule.itemType,
    quantity: rule.quantity,
    volume: rule.volume,
    unit: rule.unit,
    extended: true,
  });
  return `When buying ${itemName}, must buy ${rule.quantity}`;
};

export const generateAddressString = (address: {
  name: string;
  street: string;
  city: string;
  region: string | null;
  postalCode: string;
  country: string;
}) =>
  [
    address.name,
    address.street,
    address.city,
    address.region,
    address.postalCode,
    address.country,
  ]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(", ");

export const renderDeliveryDateTitle = (
  fulfillmentType?: OrderFulfillmentType,
): string => {
  switch (fulfillmentType) {
    case "collection":
      return "Estimated collection";
    case "courier":
    case "haulier":
      return "Estimated delivery";
    default:
      return "Estimated delivery";
  }
};

export const generateLoginLink = (): string => {
  if (window.location.pathname === ROUTES.LOGIN) return ROUTES.LOGIN;

  const currentSearchParams = new URLSearchParams(window.location.search);
  const continuePath =
    currentSearchParams.get("continue") || window.location.pathname;
  const queryParameters =
    currentSearchParams.get("queryParameters") || window.location.search;

  return withSearchParams(ROUTES.LOGIN, {
    continue: continuePath,
    ...(queryParameters ? { queryParameters } : {}),
  });
};

export const generateSignupContinueUrl = (
  supplierId?: number,
  queryParameters?: string,
) => {
  return new URLSearchParams(
    supplierId
      ? {
          supplierId: supplierId.toString(),
          continue: `/suppliers/${supplierId}`,
          ...(queryParameters && {
            queryParameters: queryParameters,
          }),
        }
      : {},
  );
};

export const getContinueUrlFromOnboardingUrlStorage = (
  onboardingContinueUrl: string | null,
) => {
  const continueUrl = onboardingContinueUrl?.split("?")[0];
  const queryParameters = onboardingContinueUrl?.split("?")[1];

  return continueUrl
    ? `${continueUrl}${queryParameters ? `?${queryParameters}` : ""}`
    : ROUTES.LANDING;
};

export const renderTaxName = (currency?: "gbp" | "sgd"): string => {
  if (currency === "sgd") return "GST";
  else return "VAT";
};

export const createOrderCommonParams = (order: {
  OrderItems?: { quantity?: number; price?: number }[];
  deliveryCharge?: number;
  discountAmount?: number;
  vatPercentage?: number;
}) => {
  return {
    orderItems: order.OrderItems
      ? order.OrderItems.map((item) => ({
          quantity: item.quantity ?? 0,
          price: item.price ?? 0,
        }))
      : [],
    deliveryCharge: order.deliveryCharge ?? 0,
    discountAmount: order.discountAmount ?? 0,
    vatPercentage: order.vatPercentage ?? 0,
  };
};

export const isPaidCheckoutOrder = ({
  paidAt,
  paymentType,
}: {
  paidAt: Date | null;
  paymentType: OrderPaymentType | null;
}) => {
  return (
    !!paidAt &&
    (paymentType === "checkout" ||
      paymentType === "stripe" ||
      paymentType === "cardPayment")
  );
};

export const createFulfillmentTypeContainerDeliveryChargeParams = (
  orderItems: Omit<BasketOrderItem, "id">[],
  fulfillmentTypeContainerDeliveryCharges?: FulfillmentTypeListResponseType[number]["FulfillmentTypeContainerDeliveryCharges"],
  deliveryChargeRules?: NonNullable<
    SupplierStorefrontV2GetResponseType["FulfillmentType"]
  >["DeliveryChargeRules"],
  flatDeliveryCharge?: number | null,
  deliveryChargeCap?: number | null,
  freeDeliveryBasketSubtotal?: number | null,
) => {
  return {
    orderItems: orderItems
      .filter((orderItem) => !!orderItem.Product.containerType)
      .map((orderItem) => ({
        volume: orderItem.Product.volume ?? null,
        displayUnit: orderItem.Product.unit ?? "",
        packQuantity: orderItem.Product.packQuantity ?? null,
        containerType: orderItem.Product.containerType ?? "",
        price: orderItem.price,
        quantity: orderItem.quantity,
      })),
    fulfillmentTypeContainerDeliveryCharges,
    deliveryChargeRules: deliveryChargeRules?.map((rule) => ({
      ...rule,
      comparator: comparatorNameToComparator(rule.comparator) as Comparator,
    })),
    flatDeliveryCharge,
    deliveryChargeCap,
    freeDeliveryBasketSubtotal,
  };
};
export type RetailerBusinessEmailsType = {
  Business: {
    invoiceEmails: string[] | null;
    email: string | null;
  };
};

export const getInvoiceEmails = ({
  connection,
  order,
  buyer,
}: {
  connection?: {
    connectionType: "prospect" | "customer";
    buyerInvoiceEmails?: string[] | null;
  };
  order?: {
    retailerInvoiceEmails: string[] | null;
  };
  buyer?: RetailerBusinessEmailsType;
}) => {
  const sanitizedBusinessInvoiceEmails = sanitizeEmailArray(
    buyer?.Business.invoiceEmails ?? [],
  );

  return order?.retailerInvoiceEmails?.some((email) => email.trim() !== "")
    ? order.retailerInvoiceEmails.filter((email) => email.trim() !== "")
    : connection?.buyerInvoiceEmails?.some((email) => email.trim() !== "")
      ? connection.buyerInvoiceEmails.filter((email) => email.trim() !== "")
      : sanitizedBusinessInvoiceEmails.length > 0 &&
          connection?.connectionType === "customer"
        ? sanitizedBusinessInvoiceEmails
        : connection?.connectionType === "customer"
          ? [buyer?.Business.email].filter(Boolean)
          : [""];
};
